import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge } from '../atoms';
import {
  suggestionsFromReading, suggestionsFromOperatingStatement, type Suggestion,
} from '../lib/scanSuggestions';
import { readableTypesList } from '../lib/extractionCopy';

/**
 * PropertyScanIntake — "Upload existing property documents to prefill these fields."
 *
 * Scan-documents PRD (2026-09-23, Melinda): at the top of Property Information in the new property
 * setup wizard, a drop zone reads the document types the extraction service lists (value notices,
 * tax bills, board orders, deeds, …) plus I&E workbooks, and turns what they say into SUGGESTIONS
 * for the form. The zone names those types from the availability endpoint, and a document the
 * reader does not recognize says so by naming them too. Nothing is saved here: each file is
 * uploaded at the client level (the property does not exist yet), read with `prefill_only` (the
 * reading is stored, no rows are staged) or, for a workbook, through the operating-statement
 * reader, and the fields come back as suggestions the wizard shows in its own inputs. On Save the
 * wizard attaches the documents to the new property and logs which fields came from which one.
 */

export const MAX_SCAN_FILES = 10;

export interface ScannedDocument {
  id: string;
  name: string;
  phase: 'uploading' | 'reading' | 'ready' | 'failed';
  docType: string | null;
  suggestions: number;
  error: string | null;
}

interface ExtractResponse {
  status?: string; extracted_type?: string | null; raw_extraction_json?: string | null; error?: string | null; queued?: boolean;
  fields?: Record<string, { value?: unknown; confidence?: number }>; docType?: string | null; prefill?: boolean;
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read this file from disk.'));
    reader.onload = () => { const r = String(reader.result || ''); const c = r.indexOf(','); resolve(c >= 0 ? r.slice(c + 1) : r); };
    reader.readAsDataURL(file);
  });
}

const isWorkbook = (file: File) => /\.xlsx?$/i.test(file.name) || /spreadsheet|excel/i.test(file.type || '');

export function PropertyScanIntake({ clientId, documents, onDocuments, onSuggestions, disabled }: {
  clientId: string;
  documents: ScannedDocument[];
  onDocuments: (update: (prev: ScannedDocument[]) => ScannedDocument[]) => void;
  onSuggestions: (update: (prev: Suggestion[]) => Suggestion[]) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const availability = useQuery<{ available: boolean; reason?: string; documentTypes?: string[] }>({
    queryKey: ['document-extract', 'availability'],
    queryFn: ({ signal }) => api.get('/api/document-extract/availability', signal),
    retry: false, staleTime: 5 * 60 * 1000,
  });
  const aiAvailable = availability.data?.available === true;
  // What the reader reads, in the server's words — never a list typed into this file.
  const readableTypes = availability.data?.documentTypes;
  useEffect(() => { if (!clientId && inputRef.current) inputRef.current.value = ''; }, [clientId]);

  const patch = (id: string, p: Partial<ScannedDocument>) => onDocuments((prev) => prev.map((d) => (d.id === id ? { ...d, ...p } : d)));

  async function awaitQueuedRead(documentId: string): Promise<ExtractResponse | null> {
    const startedAt = Date.now();
    while (Date.now() - startedAt < 120_000) {
      await new Promise((r) => setTimeout(r, 2_000));
      try {
        const history = await api.get<{ extractions: ExtractResponse[] }>(`/api/document-extract/document/${documentId}`);
        const latest = (history.extractions || [])[0];
        if (latest && latest.status && latest.status !== 'running' && latest.status !== 'queued') {
          if (latest.raw_extraction_json) {
            try { const parsed = JSON.parse(latest.raw_extraction_json); return { ...latest, fields: parsed.fields, docType: latest.extracted_type }; } catch { return latest; }
          }
          return latest;
        }
      } catch { /* transient poll failure: keep waiting */ }
    }
    return null;
  }

  async function handleFiles(files: FileList | File[] | null) {
    if (!files || !clientId) return;
    const room = MAX_SCAN_FILES - documents.length;
    const picked = Array.from(files).slice(0, Math.max(0, room));
    if (!picked.length) return;
    setBusy(true);
    try {
      for (const file of picked) {
        const workbook = isWorkbook(file);
        let id = '';
        try {
          const base64 = await toBase64(file);
          const uploaded = await api.post<{ id: string }>('/api/documents/upload', {
            filename: file.name, content_type: file.type || (workbook ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf'),
            name: file.name, client_id: clientId, file_data: base64,
            ...(workbook ? { document_type: 'Operating Statement' } : {}),
          });
          id = uploaded.id;
          onDocuments((prev) => [...prev, { id, name: file.name, phase: 'reading', docType: workbook ? 'Operating Statement' : null, suggestions: 0, error: null }]);
          let found: Suggestion[] = [];
          if (workbook) {
            const read = await api.get<{ extracted: { units?: number | null; reported_noi?: number | null; reported_noi_year?: number | null } }>(`/api/operating-statements/${id}`);
            found = suggestionsFromOperatingStatement(read.extracted, { id, name: file.name });
          } else if (aiAvailable) {
            const posted = await api.post<ExtractResponse>(`/api/document-extract/document/${id}`, { prefill_only: true });
            const read = posted.queued ? await awaitQueuedRead(id) : posted;
            if (!read) { patch(id, { phase: 'failed', error: 'Still being read. It is attached to the property; try the prefill again in a few minutes.' }); continue; }
            if (read.status === 'unrecognized') {
              // The reader worked; this is not one of its types. Say which it does read, and
              // where the manual fix is — the document is attached either way.
              const list = readableTypesList(readableTypes);
              patch(id, { phase: 'ready', docType: null, suggestions: 0, error: `Attached as Other. Not a document type the reader knows${list ? ` (it reads ${list})` : ''}. Set its type on the document after saving.` });
              continue;
            }
            if (read.status !== 'succeeded') { patch(id, { phase: 'ready', docType: null, suggestions: 0, error: read.error ? `Attached as Other: ${read.error}` : 'Attached as Other; nothing to suggest.' }); continue; }
            const docType = read.docType || read.extracted_type || null;
            found = suggestionsFromReading(docType, read.fields, { id, name: file.name });
            patch(id, { docType });
          } else {
            patch(id, { phase: 'ready', docType: null, suggestions: 0, error: 'Attached; automatic reading is off for this firm.' });
            continue;
          }
          if (found.length) onSuggestions((prev) => [...prev, ...found]);
          patch(id, { phase: 'ready', suggestions: found.length, error: found.length ? null : 'Attached; nothing on it fills these fields.' });
        } catch (err) {
          const message = err instanceof ApiClientError ? err.message : 'This document could not be read.';
          if (id) patch(id, { phase: 'failed', error: message });
          else onDocuments((prev) => [...prev, { id: `failed:${file.name}:${Date.now()}`, name: file.name, phase: 'failed', docType: null, suggestions: 0, error: message }]);
        }
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' }}>
      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled && clientId) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); if (!disabled && clientId) void handleFiles(e.dataTransfer.files); }}
        style={{
          padding: 'var(--sp-4)', border: `1px dashed ${dragging ? 'var(--brand-primary)' : 'var(--color-border)'}`, borderRadius: 'var(--r-lg)',
          background: dragging ? 'var(--color-status-green-bg, var(--color-bg))' : 'var(--color-bg)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)',
        }}
      >
        <div style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)', marginBottom: 'var(--sp-1)' }}>Upload existing property documents to prefill these fields.</div>
        <div style={{ color: 'var(--color-ink-3)', marginBottom: 'var(--sp-2)' }}>
          {aiAvailable && readableTypesList(readableTypes)
            ? <>Reads: {readableTypesList(readableTypes)}, and I&amp;E workbooks. </>
            : <>Reads I&amp;E workbooks. </>}
          Up to {MAX_SCAN_FILES} files.
          Each value shows as a suggestion you accept or dismiss; nothing is saved until you do. The documents attach to the property on save.
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/pdf,image/*,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={disabled || busy || !clientId || documents.length >= MAX_SCAN_FILES}
          onChange={(e) => handleFiles(e.target.files)}
          aria-label="Upload property documents to prefill"
        />
        {!clientId && <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Choose the client first so the documents file under it.</p>}
        {busy && <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Reading… a few seconds per document.</p>}
        {availability.data && !aiAvailable && (
          <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-status-amber)' }}>
            {availability.data.reason || 'Automatic reading is off.'} Workbooks still prefill units and NOI; other files attach without suggestions.
          </p>
        )}
      </div>
      {documents.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          {documents.map((d) => (
            <li key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }}>
              <span style={{ color: 'var(--color-ink)' }}>{d.name}</span>
              {d.docType && <Badge variant="info">{d.docType}</Badge>}
              {d.phase === 'reading' && <Badge variant="neutral">Reading…</Badge>}
              {d.phase === 'ready' && d.suggestions > 0 && <Badge variant="success">{d.suggestions} suggestion{d.suggestions === 1 ? '' : 's'}</Badge>}
              {d.phase === 'failed' && <Badge variant="danger">Not read</Badge>}
              {d.error && <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)' }}>{d.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
